import React from 'react';
import { View, StyleSheet, TextInput } from 'react-native';
import type { PaymentMethod, FeeStructure } from '@/types/residents/payment';
import type { BalanceItem } from '@/types/residents/lease';
import {
  PaymentAmountSelector,
  PaymentMethodDisplay,
  ConvenienceFeesDisplay,
} from '@/components/residents/payments/mobile/makePayment';
import { ChargeBreakdown } from '@/features/payments/components/shared/ChargeBreakdown';

interface PaymentDetailsContentProps {
  balanceDue: number;
  balanceItems?: BalanceItem[];
  selectedAmountOption: 'balance' | 'other';
  customAmount: string;
  onSelectBalance: () => void;
  onSelectOther: () => void;
  onCustomAmountChange: (value: string) => void;
  onInfoPress: () => void;
  residentName?: string;
  disabled: boolean;
  amountError?: string;
  paymentMethod: PaymentMethod | null;
  onAddPaymentMethod: () => void;
  onChangePaymentMethod: () => void;
  methodError?: string;
  feeStructure: FeeStructure;
  inputRef?: React.RefObject<TextInput | null>;
}

/**
 * Payment Details Content - First screen in payment flow
 * Shows itemized charges, amount selection, and payment method display
 */
export const PaymentDetailsContent: React.FC<PaymentDetailsContentProps> = ({
  balanceDue,
  balanceItems,
  selectedAmountOption,
  customAmount,
  onSelectBalance,
  onSelectOther,
  onCustomAmountChange,
  onInfoPress,
  residentName,
  disabled,
  amountError,
  paymentMethod,
  onAddPaymentMethod,
  onChangePaymentMethod,
  methodError,
  feeStructure,
  inputRef,
}) => {
  return (
    <View style={styles.container}>
      <View style={styles.content}>
        <ChargeBreakdown
          balanceItems={balanceItems || []}
          balanceDue={balanceDue}
          variant="marketplace"
        />

        {/* Payment Amount Section */}
        <View style={styles.section}>
          <PaymentAmountSelector
            balanceDue={balanceDue}
            selectedOption={selectedAmountOption}
            customAmount={customAmount}
            onSelectBalance={onSelectBalance}
            onSelectOther={onSelectOther}
            onCustomAmountChange={onCustomAmountChange}
            onInfoPress={onInfoPress}
            residentName={residentName}
            disabled={disabled}
            error={amountError}
            inputRef={inputRef}
          />
        </View>

        {/* Payment Method Section */}
        <View style={styles.section}>
          <PaymentMethodDisplay
            paymentMethod={paymentMethod}
            onAddPress={onAddPaymentMethod}
            onChangePress={onChangePaymentMethod}
            showExpiredError={paymentMethod?.isExpired}
            error={methodError}
          />
        </View>
      </View>

      {/* Convenience Fees Section */}
      <ConvenienceFeesDisplay feeStructure={feeStructure} />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    paddingHorizontal: 24,
    paddingVertical: 24,
    gap: 16,
  },
  section: {
    gap: 16,
  },
});

export default PaymentDetailsContent;

